import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Layout, Row, Col, theme, App } from 'antd';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Calculator from './Calculator';
import type { CalculatorData } from './Calculator';
import CalculatorSummary from './CalculatorSummary';
import BsrChart from './BsrChart';
import SalesPlan from './SalesPlan';
import type { SalesPlanData } from './SalesPlan';
import Budget from './Budget';
import type { BudgetCategory } from './Budget';
import ForecastAmazonUS from './ForecastAmazonUS';
import type { ForecastSummaryData } from './ForecastAmazonUS';
import Summary from './Summary';
import Notes from './Notes';
import CompetitorAnalysis from './CompetitorAnalysis';
import { loadSavedResult, fetchSharedResult } from '../api/client';
import { mapSalesPlan, mapCalcData } from '../utils/resultMapping';
import { sumBudget, computeCogs, computeFullUnitCost } from '../utils/budget';

const DashboardView: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [calculatorData, setCalculatorData] = useState<CalculatorData | undefined>();
  const [salesPlanData, setSalesPlanData] = useState<SalesPlanData | undefined>();
  const [budgetData, setBudgetData] = useState<BudgetCategory[] | undefined>();
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryData | null>(null);
  const [asin, setAsin] = useState('');
  const [competitorAsins, setCompetitorAsins] = useState<string[]>([]);
  const [keepaLoaded, setKeepaLoaded] = useState(false);
  const [loadedResult, setLoadedResult] = useState<Record<string, unknown> | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [notesData, setNotesData] = useState<string>('');

  useEffect(() => {
    const shareId = sessionStorage.getItem('loadShareId');
    if (shareId) {
      sessionStorage.removeItem('loadShareId');
      fetchSharedResult(shareId)
        .then((data) => {
          setLoadedResult(data);
          setLoadKey((k) => k + 1);
        })
        .catch(() => message.error(t('idea_calc.messages.load_shared_error')));
    }
  }, []);

  const handleCalcChange = useCallback((data: CalculatorData) => {
    setCalculatorData(data);
    if (data.asin) setAsin(data.asin);
    else setAsin('');
    if (data.competitorAsins?.length) setCompetitorAsins(data.competitorAsins);
    setKeepaLoaded(!!data.keepaLoaded);
  }, []);

  const handleLoadResult = useCallback(async (fileName: string) => {
    try {
      const data = await loadSavedResult(fileName + '.json');
      setLoadedResult(data);
      setLoadKey((k) => k + 1);
      setLoadedFileName(fileName);
    } catch {
      message.error(t('idea_calc.messages.load_calc_error'));
    }
  }, []);

  const calcInitial = useMemo(
    () => loadedResult ? mapCalcData(loadedResult.calculation_data as Record<string, unknown>) : undefined,
    [loadedResult],
  );
  const budgetInitial = useMemo(() => loadedResult?.budget_data as any, [loadedResult]);
  const salesPlanInitial = useMemo(() => mapSalesPlan(loadedResult?.sales_plan), [loadedResult]);
  const notesInitial = useMemo(() => loadedResult?.notes as string | undefined, [loadedResult]);

  // Calculate total budget from budget categories (matching Budget.tsx logic)
  const totalLaunchBudget = useMemo(() => {
    if (!budgetData) return 15000; // default
    const cogs = calculatorData ? computeCogs(calculatorData) : 5;
    const fullUnitCost = calculatorData ? computeFullUnitCost(calculatorData) : 0;
    return sumBudget(budgetData, cogs, fullUnitCost);
  }, [budgetData, calculatorData]);

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Header calculatorData={calculatorData} salesPlanData={salesPlanData} budgetData={budgetData} notesData={notesData} onLoadResult={handleLoadResult} loadedFileName={loadedFileName} />
      <Layout.Content
        style={{
          maxWidth: 1720,
          width: '100%',
          margin: '0 auto',
          padding: token.padding,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Row gutter={[16, 16]} id="calculator" align="stretch">
            <Col xs={24} lg={6} style={{ display: 'flex' }}>
              <Calculator key={`calc-${loadKey}`} onDataChange={handleCalcChange} initialData={calcInitial} />
            </Col>
            <Col xs={24} lg={10} id="budget" style={{ display: 'flex' }}>
              <Budget
                key={`budget-${loadKey}`}
                cogs={calculatorData ? computeCogs(calculatorData) : undefined}
                fullUnitCost={calculatorData ? computeFullUnitCost(calculatorData) : undefined}
                initialData={budgetInitial}
                onDataChange={setBudgetData}
              />
            </Col>
            <Col xs={24} lg={8} style={{ display: 'flex' }}>
              <CalculatorSummary data={calculatorData} launchBudget={totalLaunchBudget} couponPromoEnabled={calculatorData?.couponPromoEnabled} />
            </Col>
          </Row>
          {asin && keepaLoaded && <BsrChart asin={asin} />}
          {(asin || competitorAsins.length > 0) && (
            <div id="competitor-analysis">
              <CompetitorAnalysis
                mainAsin={asin}
                competitorAsins={competitorAsins}
                ourPrice={calculatorData?.sellingPrice}
                ourCogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 0}
              />
            </div>
          )}
          <div id="sales-plan">
            <SalesPlan
              key={`sp-${loadKey}`}
              onDataChange={setSalesPlanData}
              asin={asin}
              competitorAsins={competitorAsins}
              purchasePrice={calculatorData?.purchasePrice}
              initialData={salesPlanInitial}
            />
          </div>
          <div id="forecast">
            <ForecastAmazonUS
              calculatorData={calculatorData}
              salesPlanData={salesPlanData}
              launchBudget={totalLaunchBudget}
              onSummaryChange={setForecastSummary}
            />
          </div>
          <div id="summary">
            <Summary forecastSummary={forecastSummary} />
          </div>
          <div id="notes">
            <Notes key={`notes-${loadKey}`} initialNotes={notesInitial} onDataChange={setNotesData} />
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default DashboardView;
